import {
    useRef,
    type FC
} from "react";
import {
    Animated,
    View
} from "react-native";
import type {
    DataTableSelectionBarStandaloneProps
} from "./type";
import stylesheet, {
    useStyles
} from "./stylesheet";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme
} from "../../../../../core/hooks";
import type {
    NCoreUIKitIcon
} from "../../../../../types";
import {
    getSlideStyle,
    usePresence
} from "../../../../../helpers/motion";
import {
    X as CloseIcon
} from "lucide-react-native";
import {
    getTooltipInverseTheme
} from "../../../../tooltip/stylesheet";
import {
    formatNumber
} from "../../../../../helpers/locale";
import Button from "../../../../button";
import Text from "../../../../text";

const DataTableSelectionBarStandalone: FC<DataTableSelectionBarStandaloneProps> = ({
    selectedCount: selectedCountProp,
    bottomOffset: bottomOffsetProp,
    accessibilityLabel,
    customTheme,
    isVisible,
    actions,
    onClose,
    style
}) => {
    const {
        controlHeights,
        shapeConfigs,
        activeTheme,
        shadowAlpha,
        elevations,
        radiuses,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext(customTheme);

    const {
        activeLocale,
        localize
    } = NCoreUIKitLocalize.useContext();

    const {
        isReduceMotionEnabled,
        isRendered,
        progress
    } = usePresence({
        isVisible: isVisible ?? selectedCountProp > 0,
        enterIntent: "listInsert",
        exitIntent: "listRemove",
        customTheme
    });

    const inverseTheme = getTooltipInverseTheme({
        customTheme,
        activeTheme
    });

    const selectedCountRef = useRef(0);

    const dynamicStyle = useStyles({
        bottomOffset: bottomOffsetProp ?? spaces.spacingLg,
        controlHeights,
        shapeConfigs,
        shadowAlpha,
        elevations,
        radiuses,
        colors,
        spaces
    });

    if(!isRendered) {
        return null;
    }

    if(selectedCountProp > 0) {
        selectedCountRef.current = selectedCountProp;
    }

    const selectedCount = selectedCountRef.current;

    const renderCloseIcon: NCoreUIKitIcon = ({
        size
    }) => <CloseIcon
        color={colors.content.text.contrastHigh}
        size={size}
    />;

    return <Animated.View
        style={[
            stylesheet.wrapper,
            dynamicStyle.wrapper,
            getSlideStyle({
                offset: spaces.spacingLg,
                isReduceMotionEnabled,
                progress
            })
        ]}
    >
        <View
            aria-label={accessibilityLabel ?? localize("data-table-selection-group")}
            style={[
                stylesheet.bar,
                dynamicStyle.bar,
                style
            ]}
            role="toolbar"
        >
            <View
                style={[
                    stylesheet.count,
                    dynamicStyle.count
                ]}
                aria-live="polite"
                role="status"
            >
                <Text
                    customColor={colors.content.text.contrastHigh}
                    variant="labelMediumSize"
                    customTheme={customTheme}
                >
                    {
                        localize(selectedCount === 1 ? "data-table-selected-count-one" : "data-table-selected-count", [
                            formatNumber({
                                value: selectedCount,
                                locale: activeLocale
                            })
                        ])
                    }
                </Text>
            </View>
            {
                actions?.map((action) => <Button
                    customIconColor={action.icon ? colors.content.text.contrastHigh : undefined}
                    customBorderColor={colors.content.text.contrastLow}
                    customTextColor={colors.content.text.contrastHigh}
                    accessibilityLabel={action.accessibilityLabel}
                    isDisabled={action.isDisabled}
                    customTheme={inverseTheme}
                    onPress={action.onPress}
                    title={action.title}
                    icon={action.icon}
                    key={action.title}
                    variant="outline"
                    type="neutral"
                    size="small"
                />)
            }
            {
                onClose ?
                    <Button
                        customTextColor={colors.content.text.contrastHigh}
                        accessibilityLabel={localize("clear-selection")}
                        customTheme={inverseTheme}
                        icon={renderCloseIcon}
                        spreadBehaviour="free"
                        onPress={onClose}
                        variant="ghost"
                        type="neutral"
                        size="small"
                    />
                :
                    null
            }
        </View>
    </Animated.View>;
};
export default DataTableSelectionBarStandalone;
